import { faArrowRotateLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Tooltip, Typography } from '@vapor/material'
import { Fade, IconButton, Stack } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTenant } from 'src/app/hooks/context/context.hook'
import HicAmountField from 'src/app/components/hic-amount-field/hic-amount-field'
import { FormItem } from 'hic-components'
import { useTranslation } from 'react-i18next'

type ResettableTextField = {
  initialValue: number
  value?: number
  defaultValue?: number
  onChange?: (value: number | null) => void
  changeOnBlur?: true
  onResetCalled?: () => void
  resetPosition: 'RIGHT' | 'TOP' | 'LEFT'
  label?: [top: string, bottom: string] | string
  min?: number
  disabled?: boolean
}

export default function ResettableTextField(props: ResettableTextField) {
  const { currency } = useTenant()
  const { t } = useTranslation()
  const isControlled = typeof props.value != 'undefined'
  const hasDefaultValue = typeof props.defaultValue != 'undefined'

  if (isControlled && hasDefaultValue)
    console.error(
      "'ResettableTextField' contains an input with both value and defaultValue props. Input elements must be either controlled or uncontrolled (specify either the value prop, or the defaultValue prop, but not both). Decide between using a controlled or uncontrolled input element and remove one of these props. More info: https://reactjs.org/link/controlled-components",
    )

  const [internalValue, setInternalValue] = useState<number | null>(props.defaultValue ?? props.initialValue)
  const [isTouched, setIsTouched] = useState(false)

  const valueIsChanged = useMemo(() => {
    const compareWithValue = (isControlled ? props.value : internalValue) ?? 0
    if (props.initialValue === compareWithValue) return false
    else return Math.abs(props.initialValue - compareWithValue) >= 1
  }, [props.initialValue, props.value, internalValue])

  const labelElement = useMemo(() => {
    if (props.label && typeof props.label === 'string') {
      return props.label
    } else {
      return props.label ? (
        <Stack direction="column" justifyContent="flex-start" spacing={0}>
          <Typography variant="captionSmall" mt={0.5}>
            {props.label[0]}
          </Typography>
          <Typography variant="captionSmall">{props.label[1]}</Typography>
        </Stack>
      ) : (
        ''
      )
    }
  }, [props.label])

  useEffect(() => {
    if (internalValue !== props.value) setInternalValue(props.value ?? null)
  }, [props.value])

  const reset = useMemo(() => {
    return (
      <Fade in={valueIsChanged}>
        {
          <IconButton
            disabled={props.disabled}
            style={{
              pointerEvents: 'auto',
              width: props.resetPosition === 'TOP' ? '1.5rem' : '2.5rem',
            }}
            color="warning"
            size="small"
            onClick={() => {
              if (props.onChange) {
                setInternalValue(props.initialValue)
                props.onChange(props.initialValue)
              }
              if (props.onResetCalled) {
                props.onResetCalled()
              }
            }}
          >
            <Tooltip title={t('commons.reset.tooltip')}>
              <span>
                <FontAwesomeIcon icon={faArrowRotateLeft} />
              </span>
            </Tooltip>
          </IconButton>
        }
      </Fade>
    )
  }, [props.onChange, props.onResetCalled, props.disabled, props.initialValue, valueIsChanged])

  return (
    <>
      <FormItem fieldLabel={labelElement} fieldEndIcon={props.resetPosition === 'TOP' && valueIsChanged ? reset : undefined}>
        <Stack direction="row">
          {props.resetPosition === 'LEFT' && valueIsChanged ? reset : undefined}
          <HicAmountField
            size="small"
            disabled={props.disabled}
            currency={currency}
            value={isControlled ? props.value ?? null : internalValue}
            nonNegative
            onBlur={() => {
              if (props.changeOnBlur && props.onChange && isTouched) {
                props.onChange(internalValue)
                setIsTouched(false)
              }
            }}
            onChange={(value) => {
              let tmpPrice = value
              if (props.min && (tmpPrice === null || tmpPrice < props.min)) {
                tmpPrice = props.min
              }
              if (props.onChange && !props.changeOnBlur) {
                props.onChange(tmpPrice)
              }
              if ((!isControlled || props.changeOnBlur) && (tmpPrice || tmpPrice === 0) && tmpPrice !== internalValue) {
                setIsTouched(true)
                setInternalValue(tmpPrice)
              }
            }}
            style={{ pointerEvents: 'auto' }}
            sx={{ width: 110 /* fix width so the input does not shrink when the user has changed the price */ }}
          />
          {props.resetPosition === 'RIGHT' && valueIsChanged ? reset : undefined}
        </Stack>
      </FormItem>
    </>
  )
}
